import { Menu, MenuProps } from '@mui/material';
import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useState,
} from 'react';

type CustomMenuProps = Pick<
  MenuProps,
  Exclude<keyof MenuProps, 'open' | 'onClose' | 'children'>
>;

export type OpenMenuType = (
  anchorEl: Element | ((element: Element) => Element) | null,
  content: ReactNode,
  props?: CustomMenuProps,
) => void;

interface MenuContext {
  openMenu: OpenMenuType;
  closeMenu: () => void;
  menuOpen: boolean;
  menuAnchorEl?: Element | ((element: Element) => Element) | null;
  menuContent: ReactNode;
  menuProps: CustomMenuProps;
}

const MenuContext = createContext<MenuContext | undefined>(undefined);

export const MenuDisplay: FC = () => {
  const context = useContext(MenuContext);

  if (!context) {
    throw new Error('You need to wrap MenuDisplay inside MenuProvider');
  }

  const { menuOpen, menuAnchorEl, menuContent, menuProps, closeMenu } = context;

  return (
    <Menu
      {...(menuProps || {})}
      open={menuOpen}
      anchorEl={menuAnchorEl}
      onClose={closeMenu}
    >
      {menuContent}
    </Menu>
  );
};

export const MenuProvider: FC<CustomMenuProps> = ({
  children,
  ...defaultProps
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<
    Element | ((element: Element) => Element) | null
  >(null);
  const [menuContent, setMenuContent] = useState<ReactNode>(null);
  const [menuProps, setMenuProps] = useState<CustomMenuProps>({});

  const openMenu: OpenMenuType = (anchorEl, content, props) => {
    setMenuAnchorEl(anchorEl);
    setMenuContent(content);
    setMenuProps(props || {});
    setMenuOpen(true);
  };
  const closeMenu = () => {
    setMenuOpen(false);
    setTimeout(() => {
      setMenuAnchorEl(null);
      setMenuContent(null);
    }, 200);
  };

  return (
    <MenuContext.Provider
      value={{
        openMenu,
        closeMenu,
        menuOpen,
        menuAnchorEl,
        menuContent,
        menuProps: { ...defaultProps, ...menuProps },
      }}
    >
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = (): Pick<MenuContext, 'openMenu' | 'closeMenu'> => {
  const context = useContext(MenuContext);
  if (!context) {
    throw new Error('You need to wrap useMenu inside MenuProvider');
  }
  const { openMenu, closeMenu } = context;
  return { openMenu, closeMenu };
};
